import { EgmCardStatus, useBlockCard, useUnblockCard, useWealthOverview } from '@egw/api'
import { ToggleSwitch } from '@egw/components'
import { sendEvent } from '@egw/events'
import { useTranslation } from '@egw/i18n'
import { InformationCircleIcon } from '@heroicons/react/16/solid'
import { useState, type FC } from 'react'

export interface DebitCardAttributeProps {
  cardId: string | undefined
  cardStatus: string | undefined
  alias: string
}

const DisableCardAttribute: FC<DebitCardAttributeProps> = ({ cardId, cardStatus, alias }) => {
  const { t } = useTranslation('features-account')

  const [toggleSwitchOn, setToggleSwitchOn] = useState(Boolean(cardStatus === EgmCardStatus.SUSPENDED_BY_USER))
  const [disableCardError, setDisableCardError] = useState(false)
  const [enableCardError, setEnableCardError] = useState(false)

  const { refetch } = useWealthOverview({ enabled: false })

  const onMutationSuccess = async (): Promise<void> => {
    setToggleSwitchOn((prev) => !prev)
    await refetch()
  }

  const { mutate: blockCardMutation, isLoading: isLoadingBlocking } = useBlockCard({
    onSuccess: (data) => {
      if (data.__typename === 'CardStatusUpdateSuccess') {
        sendEvent('eg_lockCardOn_success', {
          eventSource: 'egWealth',
          category: 'card-lock',
          subCategory: 'result',
        })
        void onMutationSuccess()
      }
      if (data.__typename === 'CardStatusUpdateError') {
        sendEvent('eg_lockCardOn_fail', {
          eventSource: 'egWealth',
          category: 'card-lock',
          subCategory: 'result',
        })
        setDisableCardError(true)
      }
    },
  })

  const { mutate: unblockCardMutation, isLoading: isLoadingUnblocking } = useUnblockCard({
    onSuccess: (data) => {
      if (data.__typename === 'CardStatusUpdateSuccess') {
        sendEvent('eg_lockCardOff_success', {
          eventSource: 'egWealth',
          category: 'card-lock',
          subCategory: 'result',
        })
        void onMutationSuccess()
      }
      if (data.__typename === 'CardStatusUpdateError') {
        sendEvent('eg_lockCardOff_fail', {
          eventSource: 'egWealth',
          category: 'card-lock',
          subCategory: 'result',
        })
        setEnableCardError(true)
      }
    },
  })

  const blockCardHandler = (): void => {
    sendEvent('user_lockCardOn_click', {
      eventSource: 'egWealth',
      category: 'card-lock',
      subCategory: 'intent',
    })
    void blockCardMutation({ cardId: cardId! })
  }

  const unblockCardHandler = (): void => {
    sendEvent('user_lockCardOff_click', {
      eventSource: 'egWealth',
      category: 'card-lock',
      subCategory: 'intent',
    })
    void unblockCardMutation({ cardId: cardId! })
  }

  const clickHandler = (): void => {
    toggleSwitchOn ? unblockCardHandler() : blockCardHandler()
  }

  return (
    <div
      id="disableCardAttribute"
      className="!text-base max-md:px-2 py-4 border-b transition-all duration-200 cursor-pointer border-light"
    >
      <div className="flex w-full items-center justify-between">
        <span className="mr-2.5">{t('debit_card.disable_card')}</span>
        <ToggleSwitch
          id="disableDebitCard"
          isLoading={isLoadingBlocking || isLoadingUnblocking}
          isOn={toggleSwitchOn}
          disabled={isLoadingBlocking || isLoadingUnblocking}
          onChange={clickHandler}
        />
      </div>
      {enableCardError || disableCardError ? (
        <div className="flex bg-alertErrorBg p-3 rounded-md">
          <InformationCircleIcon className="w-6 h-6 fill-red-500" />
          <p className="text-sm text-alertErrorText ml-2">
            {disableCardError ? t('debit_card.block_card_error') : t('debit_card.unblock_card_error')}
          </p>
        </div>
      ) : null}
    </div>
  )
}

export default DisableCardAttribute
